import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { useCategories } from '@/hooks/useCategories';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useBusinessParameters } from '@/hooks/useParameters';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useBusiness } from '@/contexts/BusinessContext';
import { WeeklyHours, createDefaultOpeningHours } from '@communitly/shared';
import { 
  encodeHashData, 
  decodeHashData, 
  validateHashData, 
  cleanFormData, 
  updateUrlHash, 
  getCurrentUrlHash 
} from '@/lib/hashUtils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Stepper } from '@/components/ui/stepper';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InfoIcon, Store, ArrowRight, House } from 'lucide-react';
import { CommunityAccessCheck } from "@/components/CommunityAccessCheck";
import { BusinessRegistrationLeftPanel } from '@/components/business/BusinessRegistrationLeftPanel';
import BusinessBasicInfoForm from '@/components/business/BusinessBasicInfoForm';
import BusinessContactForm from '@/components/business/BusinessContactForm';
import BusinessAddressForm from '@/components/business/BusinessAddressForm';
import BusinessRegistrationOpeningHoursForm from '@/components/business/BusinessRegistrationOpeningHoursForm';
import BusinessRegistrationDiscountForm from '@/components/business/BusinessRegistrationDiscountForm';
import BusinessVerificationForm from '@/components/business/BusinessVerificationForm';
import BusinessReviewForm from '@/components/business/BusinessReviewForm';

// Define type for form data
interface BusinessFormData {
  name: string;
  slug: string;
  category_id: string;
  description: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  facebook_url: string;
  instagram_url: string;
  tiktok_url: string;
  google_maps_url: string;
  address_line1: string;
  address_line2: string;
  city: string;
  county: string;
  postal_code: string;
  country: string;
  opening_hours: WeeklyHours;
  businessType: 'free' | 'paid';
  discount_amount: number;
  is_percentage: boolean;
  applies_to_all: boolean;
  discount_description: string;
  application_comments: string;
}

// Define type for serialized data in the URL hash
interface SerializedFormData extends BusinessFormData {
  currentStep: number;
}

// Form validator function
const isValidFormData = (data: any): boolean => {
  // Basic validation to ensure it's an object with expected properties
  return (
    typeof data === 'object' && 
    data !== null && 
    'currentStep' in data && 
    typeof data.currentStep === 'number' &&
    data.currentStep >= 0 &&
    data.currentStep <= 6
  );
};

const steps = [
  { id: 'basic-info', title: 'Business Details' },
  { id: 'contact', title: 'Contact' },
  { id: 'address', title: 'Address' },
  { id: 'opening-hours', title: 'Opening Hours' },
  { id: 'discount', title: 'Discount' },
  { id: 'verification', title: 'Verification' },
  { id: 'review', title: 'Review' }
];

const defaultFormData: BusinessFormData = {
  name: '',
  slug: '',
  category_id: '',
  description: '',
  contact_email: '',
  contact_phone: '',
  website: '',
  facebook_url: '',
  instagram_url: '',
  tiktok_url: '',
  google_maps_url: '',
  address_line1: '',
  address_line2: '',
  city: '',
  county: '',
  postal_code: '',
  country: 'United Kingdom',
  opening_hours: createDefaultOpeningHours(),
  businessType: 'paid',
  discount_amount: 10,
  is_percentage: true,
  applies_to_all: true,
  discount_description: '',
  application_comments: ''
};

// Progress Indicators Component
const ProgressIndicators = ({ currentStep }: { currentStep: number }) => {
  const totalSteps = 7;
  
  return (
    <div className="flex justify-center items-center space-x-3 mt-8 mb-6">
      {Array.from({ length: totalSteps }, (_, index) => (
        <div
          key={index}
          className={`w-3 h-3 rounded-full transition-all duration-200 ${
            index <= currentStep
              ? 'bg-primary'
              : 'border-2 border-muted-foreground/30 bg-transparent'
          }`}
        />
      ))}
    </div>
  );
};

const RegisterBusinessContent = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<BusinessFormData>(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [checkingBusiness, setCheckingBusiness] = useState(true);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [existingBusiness, setExistingBusiness] = useState(null);
  const [hashInitialized, setHashInitialized] = useState(false);
  
  const { user } = useAuth();
  const { currentCommunity } = useCommunity();
  const { data: categoriesData } = useCategories();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { forceDiscount, isLoading: isLoadingParams } = useBusinessParameters();
  const { refreshBusinessData } = useBusiness();

  // Set document title
  const communityName = currentCommunity?.name ?? "Community Platform";
  useDocumentTitle(`Register Business - ${communityName}`);
  
  // Check if user profile is complete
  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (!user) return;
      
      setCheckingProfile(true);
      try {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('firstname, lastname, country, town')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        // If profile doesn't exist or is incomplete, redirect to complete registration with returnTo parameter
        if (!profile || !profile.firstname || !profile.lastname || !profile.country || !profile.town) {
          navigate('/complete-registration?returnTo=/register-business', { replace: true });
          return;
        }
      } catch (error) {
        navigate('/complete-registration?returnTo=/register-business', { replace: true });
        return;
      } finally {
        setCheckingProfile(false);
      }
    };

    if (user) {
      checkProfileCompletion();
    }
  }, [user, navigate]);
  
  // Function to initialize from hash data
  const initializeFromHash = (): boolean => {
    // Get current hash data
    const hashData = getCurrentUrlHash();
    
    if (hashData) {
      try {
        const decodedData = decodeHashData<SerializedFormData>(hashData);
        
        const validatedData = validateHashData<SerializedFormData>(decodedData, isValidFormData);
        
        if (validatedData) {
          // Restore form state from URL hash
          const { currentStep: restoredStep, ...restoredFormData } = validatedData;
          
          // Update state with hash data
          setCurrentStep(restoredStep);
          setFormData({...defaultFormData, ...restoredFormData});
          setHashInitialized(true);
          return true;
        }
      } catch (error) {
        // Silent handling of hash parsing errors
      }
    }
    return false;
  };
  
  // Function to update URL hash with current form state
  const syncFormDataToHash = (newFormData: BusinessFormData, newStep: number) => {
    const dataToSerialize: SerializedFormData = {
      ...newFormData,
      currentStep: newStep
    };
    
    // Clean the data before encoding (remove empty/null values)
    const cleanedData = cleanFormData(dataToSerialize);
    
    // Update URL hash without causing navigation
    const encodedData = encodeHashData(cleanedData);
    updateUrlHash(encodedData);
  };
  
  // Initialize form data from URL hash or check for existing business
  useEffect(() => {
    // Only run this effect once when component mounts
    if (hashInitialized || checkingProfile) return;
    
    // Check if user is authenticated
    if (!user) {
      return;
    }
    
    const checkForBusinessData = async () => {
      setCheckingBusiness(true);
      
      // Try to load from hash first
      if (initializeFromHash()) {
        setCheckingBusiness(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('id, name, is_active, is_listed')
          .eq('user_id', user.id)
          .single();
        
        if (error && error.code !== 'PGRST116') {
          throw error;
        }
        
        setExistingBusiness(data);
      } catch (error) {
        // Silent error handling for business checking
      } finally {
        setCheckingBusiness(false);
      }
    };
    
    // Run the business data check
    checkForBusinessData();
    
  }, [user, hashInitialized, checkingProfile]);
  
  // Keep form data in sync when changing step
  useEffect(() => {
    // Only sync when we have form data and not checking/loading
    if (!checkingBusiness && !checkingProfile && !isLoadingParams && formData) {
      syncFormDataToHash(formData, currentStep);
    }
  }, [currentStep, formData, checkingBusiness, checkingProfile, isLoadingParams]);
  
  const handleStepChange = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < 7 && stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };
  
  const updateFormData = (data: Partial<BusinessFormData>) => {
    const newFormData = { ...formData, ...data };
    setFormData(newFormData);
  };
  
  const nextStep = () => {
    if (currentStep < 6) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      window.scrollTo(0, 0);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      window.scrollTo(0, 0);
    }
  };
  
  const handleSubmit = async () => {
    if (!user || !currentCommunity) return;
    
    // Validate that a discount is provided if required by community settings (only for paid businesses)
    if (formData.businessType === 'paid' && forceDiscount && (!formData.discount_amount || formData.discount_amount <= 0)) {
      toast({
        variant: "destructive",
        title: "Discount required",
        description: "You must provide a discount to register your business in this community.",
      });
      // Navigate back to the discount step
      setCurrentStep(4);
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare discount dates
      const discountStartDate = formData.businessType === 'paid' ? new Date().toISOString().split('T')[0] : null;
      const discountEndDate = formData.businessType === 'paid' ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : null; // 1 year from now
      
      // Prepare business data object
      const businessData = {
        name: formData.name,
        description: formData.description,
        slug: formData.slug, // Pass the base slug from the form
        category_id: formData.category_id,
        community_id: currentCommunity.id,
        user_id: user.id,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        address_line1: formData.address_line1,
        address_line2: formData.address_line2 || null,
        city: formData.city,
        county: formData.county,
        postal_code: formData.postal_code?.toUpperCase() || '',
        country: formData.country,
        website: formData.website || null,
        facebook_url: formData.facebook_url || null,
        instagram_url: formData.instagram_url || null,
        tiktok_url: formData.tiktok_url || null,
        google_maps_url: formData.google_maps_url || null,
        opening_hours: formData.opening_hours,
        application_comments: formData.application_comments || null
      };

      // Prepare discount data object
      const discountData = {
        discount_amount: formData.businessType === 'paid' ? formData.discount_amount : 0,
        is_percentage: formData.is_percentage || true,
        applies_to_all: formData.applies_to_all || true,
        description: formData.businessType === 'paid' ? formData.discount_description : null,
        type: formData.businessType === 'paid' ? 'Paid' : 'Free'
      };

      // Call the Edge Function instead of RPC
      const { data, error } = await supabase.functions.invoke(
        'submit-business-registration',
        {
          body: {
            business_data: businessData,
            discount_data: discountData
          }
        }
      );

      if (error) throw error;

      // Clear form data from URL hash after successful submission
      updateUrlHash('');

      // Important: Refresh the business context data so the UI knows the user now has a business
      try {
        await refreshBusinessData();
        
        toast({
          title: "Registration submitted for approval! 🎉",
          description: "Your business information has been submitted and is awaiting administrator review.",
        });

        // Navigate to business admin page after a small delay to ensure context is updated
        setTimeout(() => {
          navigate('/admin/my-business');
        }, 300);
      } catch (refreshError) {
        // Show error but still try to navigate
        toast({
          title: "Registration successful",
          description: "Your business was registered, but we had trouble updating the page. Please refresh if needed.",
        });
        
        navigate('/admin/my-business');
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Oops! Something went wrong",
        description: error.message || "We couldn't register your business. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <BusinessBasicInfoForm 
            formData={formData} 
            updateFormData={updateFormData}
            categories={categoriesData?.categories || []}
            onNext={nextStep}
          />
        );
      case 1:
        return (
          <BusinessContactForm 
            formData={formData} 
            updateFormData={updateFormData}
            onNext={nextStep}
            onPrev={prevStep}
          />
        );
      case 2:
        return (
          <BusinessAddressForm 
            formData={formData} 
            updateFormData={updateFormData}
            onNext={nextStep}
            onPrev={prevStep}
          />
        );
      case 3:
        return (
          <BusinessRegistrationOpeningHoursForm 
            formData={formData} 
            updateFormData={updateFormData}
            onNext={nextStep}
            onPrev={prevStep}
          />
        );
      case 4:
        return (
          <BusinessRegistrationDiscountForm 
            formData={formData} 
            updateFormData={updateFormData}
            onNext={nextStep}
            onPrev={prevStep}
          />
        );
      case 5:
        return (
          <BusinessVerificationForm 
            formData={formData} 
            updateFormData={updateFormData}
            onNext={nextStep}
            onPrev={prevStep}
          />
        );
      case 6:
        return (
          <BusinessReviewForm 
            formData={formData}
            categories={categoriesData?.categories || []}
            onSubmit={handleSubmit}
            onPrev={prevStep}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };
  
  if (checkingBusiness || checkingProfile || isLoadingParams) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="shadow-md w-full max-w-md">
          <CardContent className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-3 border-current border-t-transparent text-primary" />
            <span className="ml-3 text-lg">
              {checkingProfile ? "Checking profile..." : "Checking business status..."}
            </span>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (existingBusiness) {
    return (
      <div className="min-h-screen grid grid-cols-1 md:grid-cols-[42%_58%] relative">
        {/* Home Navigation Link */}
        <Link
          to="/"
          className="absolute top-6 left-6 z-50 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-background hover:border-border transition-all duration-200 shadow-sm hover:shadow-md"
          aria-label="Go to home page"
        >
          <House className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
          <span className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Home</span>
        </Link>

        {/* Left Panel - Only visible on medium and larger screens */}
        <div className="hidden md:flex bg-[hsl(var(--primary)/0.03)] relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-20 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-40 right-20 w-48 h-48 bg-primary/8 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-10 w-24 h-24 bg-primary/6 rounded-full blur-2xl"></div>
          </div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center px-12 py-16">
            <BusinessRegistrationLeftPanel />
          </div>
        </div>

        {/* Right Panel - Main content */}
        <motion.div 
          className="w-full flex items-center justify-center p-6 md:p-12 overflow-y-auto bg-background"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-full max-w-2xl">
            <Card className="rounded-2xl shadow-lg border-0">
              <CardHeader>
                <CardTitle className="text-2xl md:text-3xl">You already have a registered business</CardTitle>
                <CardDescription className="text-base mt-2">You can only register one business per account</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="bg-secondary/80 p-4 rounded-lg border">
                  <div className="flex items-center space-x-2 mb-2">
                    <Store className="h-5 w-5" />
                    <h3 className="text-lg font-semibold">Your business: {existingBusiness.name}</h3>
                  </div>
                  <p className="text-sm">
                    Status: <span className="font-medium">
                      {existingBusiness.is_active && existingBusiness.is_listed ? 'Published' : 'Pending Approval'}
                    </span>
                  </p>
                </div>
                
                <p className="text-muted-foreground text-base leading-relaxed">
                  You already have a registered business in our system. Each user can only have one business at a time.
                  Visit your business dashboard to manage your existing business.
                </p>
                
                <div className="flex justify-end pt-4">
                  <Button onClick={() => navigate('/admin/my-business')} size="lg" className="gap-2">
                    Go to My Business
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[42%_58%] relative">
      {/* Home Navigation Link */}
      <Link
        to="/"
        className="absolute top-6 left-6 z-50 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-background hover:border-border transition-all duration-200 shadow-sm hover:shadow-md"
        aria-label="Go to home page"
      >
        <House className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
        <span className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Home</span>
      </Link>

      {/* Left Panel - Only visible on medium and larger screens */}
      <div className="hidden md:flex bg-[hsl(var(--primary)/0.03)] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 right-20 w-48 h-48 bg-primary/8 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-10 w-24 h-24 bg-primary/6 rounded-full blur-2xl"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-16">
          <BusinessRegistrationLeftPanel />
        </div>
      </div>

      {/* Right Panel - Main content */}
      <motion.div 
        className="w-full flex items-center justify-center p-6 md:p-12 overflow-y-auto bg-background"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-full max-w-2xl">
          <Card className="rounded-2xl shadow-lg border-0">
            <CardContent className="p-8 md:p-12">
              <div className="mb-8">
                <h1 className="text-3xl font-bold mb-6">Register Your Business</h1>
              </div>
              
              {/* Form content */}
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {renderStep()}
                
                {/* Progress Indicators at the bottom */}
                <ProgressIndicators currentStep={currentStep} />
              </motion.div>
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </div>
  );
};

// Add the CommunityAccessCheck wrapper
const RegisterBusiness = () => {
  return (
    <CommunityAccessCheck>
      <RegisterBusinessContent />
    </CommunityAccessCheck>
  );
};

export default RegisterBusiness;
